<template>
  <div
    class="tw:mt-4 tw:flex tw:justify-between cursor-pointer"
    :class="{ 'tw:mb-4': !showCustomAttributes }"
    @click="toggleCustomAttributes"
  >
    <h3 class="tw:font-bold">
      {{ $t("modals.saml_integration_modal.custom_attributes") }}
    </h3>

    <div
      class="tw:transition-all tw:duration-300"
      :class="{
        'tw:rotate-180': showCustomAttributes,
      }"
    >
      <BaseIcon :icon="'angle-down'" />
    </div>
  </div>
  <div v-if="showCustomAttributes">
    <template v-if="teams.length">
      <h3 class="tw:mt-4">
        {{ $t("modals.saml_integration_modal.team_mappings") }}
      </h3>
      <div class="tw:mt-4">
        {{ $t("modals.saml_integration_modal.custom_attributes_details_a") }}
      </div>

      <div>
        <p class="tw:mt-4">
          {{ $t("modals.saml_integration_modal.add_new_team") }}
        </p>
        <div
          class="tw:mb-4 tw:grid tw:grid-cols-[1fr_1fr_0.1fr] tw:items-center tw:gap-4"
        >
          <InputText
            v-model="selectedOptions.customAttribute"
            new-design
            :placeholder="
              $t(
                'modals.saml_integration_modal.custom_attribute_value_placeholder'
              )
            "
          />
          <InputSelect
            v-model="selectedOptions.selectedTeam"
            new-design
            option-label="name"
            :options="teams"
            :placeholder="$t('modals.saml_integration_modal.select_team')"
          />

          <BaseButton
            color="ds-denim-3"
            :label="$t('global.add')"
            @click="addCustomAttribute"
          />
        </div>
      </div>

      <div
        v-if="Object.keys(samlIntegration.customAttributes?.teams || {}).length"
        class="tw:mt-4 tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 mx-auto w-full max-w-lg"
      >
        <div class="tw:p-2 tw:font-bold tw:text-denim-700">
          <div class="tw:grid tw:grid-cols-[1fr_1fr_0.1fr]">
            <div>
              {{ $t("modals.saml_integration_modal.custom_attribute_value") }}
            </div>
            <div>
              {{ $t("modals.saml_integration_modal.custom_attribute_team") }}
            </div>
            <div />
          </div>
        </div>

        <BaseSeparator />

        <div class="tw:mx-2">
          <div
            v-for="(attributeName, index) in Object.keys(
              samlIntegration.customAttributes?.teams || {}
            )"
            :key="attributeName"
          >
            <div
              class="tw:grid tw:grid-cols-[1fr_1fr_0.1fr] tw:items-center tw:py-2"
            >
              <div class="text-gray-700">{{ attributeName }}</div>
              <div class="font-medium text-gray-900">
                {{ samlIntegration.customAttributes.teams[attributeName].name }}
              </div>
              <BaseButton
                dense
                icon="delete"
                new-design
                @click="removeTeam(String(attributeName))"
              />
            </div>

            <BaseSeparator
              v-if="
                index < Object.keys(samlIntegration.customAttributes).length - 1
              "
            />
          </div>
        </div>
      </div>
    </template>
    <UserRoles v-model="samlIntegration" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref } from "vue";

import {
  BaseButton,
  BaseIcon,
  BaseSeparator,
  InputSelect,
  InputText,
} from "shared/components/base";
import UserRoles from "shared/components/SamlIntegration/UserRoles.vue";
import type { SamlIntegration } from "shared/resources";
import { useUserStore } from "shared/stores/user";
import type { OrganisationTeam } from "shared/stores/user/types";

const { organisation } = storeToRefs(useUserStore());

const samlIntegration = defineModel<SamlIntegration>({
  required: true,
});

const showCustomAttributes = ref(true);

interface SelectedOptions {
  customAttribute: string;
  selectedTeam: OrganisationTeam;
}

const selectedOptions = ref<SelectedOptions>({
  customAttribute: "",
  selectedTeam: {} as OrganisationTeam,
});

interface CustomAttribute {
  id: number;
  name: string;
}

const teams = samlIntegration.value.customAttributes?.length
  ? organisation.value.organisation_teams.filter((team) =>
      Object.values(samlIntegration.value.customAttributes).some(
        (attr: CustomAttribute) => attr.id !== team.id
      )
    )
  : organisation.value.organisation_teams || [];

function addCustomAttribute() {
  samlIntegration.value.customAttributes =
    samlIntegration.value.customAttributes || {};

  samlIntegration.value.customAttributes.teams =
    samlIntegration.value.customAttributes.teams || {};

  samlIntegration.value.customAttributes.teams[
    selectedOptions.value.customAttribute
  ] = {
    id: selectedOptions.value.selectedTeam.id,
    name: selectedOptions.value.selectedTeam.name,
  };

  selectedOptions.value.customAttribute = "";
  selectedOptions.value.selectedTeam = {} as OrganisationTeam;
}

function removeTeam(key: string) {
  delete samlIntegration.value.customAttributes.teams[key];
}

function toggleCustomAttributes() {
  showCustomAttributes.value = !showCustomAttributes.value;
}
</script>
